import React, { useMemo, useState } from "react";
import "./ProcessingDashboard.css";
import "../../dashboard/components/dashboard-sections.css";

import { AddProcessingEmployeeData } from "./AddProcessingEmployeeData";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import { ProcessingIconSvg } from "../../../svgFiles/ProcessingIconSvg";
import { OutputIconSvg } from "../../../svgFiles/OutputIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import { UtilityIconSvg } from "../../../svgFiles/UtilityIconSvg";
import { FlameIconSvg } from "../../../svgFiles/FlameIconSvg";
import { CostIconSvg } from "../../../svgFiles/CostIconSvg";
import {
  useGetProcessingEmployeeQuery,
  useGetProcessingSummaryQuery,
} from "./processingApi";
import { useSearchGasConsumptionsQuery } from "../cooking/gasconsumption/gasConsumptionApi";
import { useGetAllWorkingAreasQuery } from "../../../app/globalApi";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";

const ProcessingDashboard = () => {
  const [showAddEmployment, setShowAddEmployment] = useState(false);  
  const currentDate = getCurrentDate('YYYY-MM-DD');
  const [date, setDate] = useState(currentDate);
  const [tab, setTab] = useState("dashboard");
  const { _t } = useTranslator();

  const { data: summaryData } = useGetProcessingSummaryQuery({ tab, date });
  const { data: gasConsumptionData } = useSearchGasConsumptionsQuery({
    page: 1,
    date,
  });
  const { data: employeeSummaryData } = useGetProcessingEmployeeQuery({
    date,
  });
  const { data: workingAreasData, isLoading: isWorkingAreasLoading } =
    useGetAllWorkingAreasQuery();

  const receivingAreaId = useMemo(() => {
    if (!workingAreasData?.data) return null;
    const receivingArea = workingAreasData.data.find(
      (area) => area.name === "Processing & Cooking Area"
    );
    return receivingArea ? receivingArea.id : null;
  }, [workingAreasData]);

  const displayDay = currentDate === date ? _t("today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT);

  const processingItems = [
    { label: "Cases Processed", value: summaryData?.data.total_cases_processed || 0 },
    { label: "Cases Combined", value: 0 },
    { label: "Total Cases Used", value: 0 },
    { label: "Cases Used With Trash", value: 0 },
    { label: "Treys Strips", value: 0 },
    { label: "Treys Cheeks", value: 0 },
    { label: "Total Treys", value: summaryData?.data.total_treys || 0 },
    { label: "Carts", value: summaryData?.data.total_carts || 0 },
  ];

  const metricsItems = [
    { label: "Case Peeled / Per KG", value: "0.00" },
    { label: "Case In Processing / Per KG", value: "0.00" },
    { label: "Cases Per Peeler", value: "0.00" },
    { label: "Treys Per Peeler", value: "0.00" },
    { label: "Total Cases Per Cart", value: "0.00" },
    { label: "Treys Per Case Peeled", value: summaryData?.data.total_trey_per_case?.toFixed(2) || "0.00" },
    { label: "Kilos Per Cart", value: "0.00" },
    { label: "Kilos Per Trey", value: "0.00" },
    { label: "Case Per Trey Entered", value: "0.00" },
    { label: "Case Per Cart", value: summaryData?.data.total_cart_per_case?.toFixed(2) || "0.00" },
    { label: "Treys Per Cart", value: "0.00" },
  ];

  const peelerMetricsItems = [
    { label: "Peelers", value: 0 },
    { label: "Cases Processed Per Hour", value: "0.00" },
    { label: "Cases Processed Per Person Per Hour", value: "0.00" },
    { label: "Cases Used In Processing Per Hour", value: "0.00" },
  ];

  const trashItems = [
    { label: "Trash Sorting", value: "0.00" },
    { label: "Trash Processing", value: "0.00" },
    { label: "Total", value: "0.00" },
  ];
  
  const stripsItems = [
    { label: "Strip Total (Night Plus Day) / KG", value: "0.00" },
    { label: "Prime Strip First Sort / KG", value: "0.00" },
    { label: "Reprocessing Made Prime Strip / KG", value: "0.00" },
    { label: "Humid Strip / KG", value: "0.00" },
    { label: "Humid %", value: "0.00" },
  ];

  const cheeksItems = [
    { label: "Cheek Total / KG", value: "0.00" },
    { label: "Prime Cheek First Sort / KG", value: "0.00" },
    { label: "Reprocessing Made Prime Cheek / KG", value: "0.00" },
    { label: "Humid Cheek / KG", value: "0.00" },
    { label: "Humid %", value: "0.00" },
  ];

  const totalItems = [
    { label: "Total Taken Off / KG", value: "0.00" },
    { label: "Prime Total With Reprocessing / KG", value: "0.00" },
    { label: "% Prime", value: "0.00" },
  ];

  const gasRecord = gasConsumptionData?.data?.[0] || null;

  const parseNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatMetric = (value, fixed = 2) => {
    const parsed = parseNumber(value);
    return parsed.toLocaleString(undefined, {
      minimumFractionDigits: fixed,
      maximumFractionDigits: fixed,
    });
  };

  const ovens = Array.isArray(gasRecord?.ovens)
    ? [...gasRecord.ovens].sort(
        (a, b) => parseNumber(a?.oven_number) - parseNumber(b?.oven_number)
      )
    : [];

  const getOvenLiters = (ovenNo) => {
    const oven = ovens.find(
      (item) => String(item?.oven_number) === String(ovenNo)
    );
    return parseNumber(oven?.liters_consumed);
  };

  const totalLitersUsed =
    parseNumber(gasRecord?.total_liters_consumed) ||
    ovens.reduce((sum, oven) => sum + parseNumber(oven?.liters_consumed), 0);

  const pricePerLiterWithIva = parseNumber(gasRecord?.price_per_l);

  const totalGasPaid =
    parseNumber(gasRecord?.total_group_cost) ||
    ovens.reduce((sum, oven) => sum + parseNumber(oven?.total_cost), 0);

  const ivaGasPaid =
    parseNumber(gasRecord?.iva_gas_paid) || parseNumber(gasRecord?.iva_paid);

  const kgProcessed =
    parseNumber(summaryData?.data?.kg_processed) ||
    parseNumber(summaryData?.data?.total_kg_processed) ||
    parseNumber(summaryData?.data?.total_kg_taken_off);

  const gasCostPerKg =
    kgProcessed > 0 ? totalGasPaid / kgProcessed : parseNumber(gasRecord?.gas_cost_per_kg_processed);

  const usdValue =
    parseNumber(gasRecord?.usd) || parseNumber(gasRecord?.usd_amount);

  const weeklyAverage =
    parseNumber(gasRecord?.weekly_average) ||
    parseNumber(gasRecord?.weekly_avg);

  const gasConsumptionRows = [
    { label: "Oven 1", value: formatMetric(getOvenLiters(1)) },
    { label: "Oven 2", value: formatMetric(getOvenLiters(2)) },
    { label: "Oven 3", value: formatMetric(getOvenLiters(3)) },
    { label: "Oven 4", value: formatMetric(getOvenLiters(4)) },
    { label: "Oven 5", value: formatMetric(getOvenLiters(5)) },
    { label: "Oven 6", value: formatMetric(getOvenLiters(6)) },
    { label: "Evaperator", value: formatMetric(parseNumber(gasRecord?.evaperator)) },
    { label: "Total Liters Used", value: formatMetric(totalLitersUsed) },
    {
      label: "Price (With IVA) / Per Liter",
      value: formatMetric(pricePerLiterWithIva),
    },
    { label: "IVA Gas Paid", value: formatMetric(ivaGasPaid) },
    { label: "Total Gas Paid", value: formatMetric(totalGasPaid) },
    { label: "KG Processerd", value: formatMetric(kgProcessed) },
    {
      label: "Gas Cost Per KG Processed",
      value: formatMetric(gasCostPerKg),
    },
    { label: "USD", value: formatMetric(usdValue) },
    { label: "Weekly Average", value: formatMetric(weeklyAverage) },
  ];

  const renderShiftCard = ({ title, icon, badge, badgeStyle, colHeader = "Value", rows }) => (
    <div className="pp-shift-card cmn_card">
      <div className="pp-shift-header">
        <div className="pp-shift-title-row">
          {icon && <span className="pp-shift-icon">{icon}</span>}
          <span className="pp-shift-title">{title}</span>
        </div>
        {badge && (
          <span className="pp-shift-badge" style={badgeStyle}>{badge}</span>
        )}
      </div>
      <div className="pp-shift-col-header">
        <span>Category</span>
        <span>{colHeader}</span>
      </div>
      {rows.map((row, i) => {
        const isTotal = /total/i.test(row.label);
        return (
          <div
            key={i}
            className="pp-shift-row"
          >
            <span>{row.label}</span>
            <span style={isTotal ? { color: "#ee7225", fontWeight: 700 } : {}}>{row.value}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {showAddEmployment && (
        <AddProcessingEmployeeData
          show={showAddEmployment}
          setShow={setShowAddEmployment}
          record={employeeSummaryData?.data}
        />
      )}

      <div className="processing_dashboard_toolbar align-items-center mb-4">
        <h5 className="processing_dashboard_toolbar_title m-0">
          {_t("Processing Overview", "Processing Overview")}
        </h5>
        <div className="processing_filter_group">          
          <DateRangeFilter setDate={setDate} triggerId="processing-date-range-filter" />          
        </div>
      </div>

      <div className="d-flex flex-column gap-4">

        {/* ── Group 1: Processing Flow ─────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><ProcessingIconSvg size={18} color="#60a5fa" />{_t("processing_flow", "Processing Flow")}</span>}>
            <div className="pp-brokering-grid">
              {processingItems.map((item, i) => (
                <div key={i} className="pp-brokering-box">
                  <div className="pp-brokering-box-label">{item.label}</div>
                  <div className="pp-brokering-box-value">{item.value}</div>
                </div>
              ))}
            </div>
        </DashboardSection>

       

        {/* ── Group 3: Output & Summary ────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><OutputIconSvg size={18} color="#4ade80" />{_t("output_and_summary", "Output and Summary")}</span>}>
          <div className="pp-shifts-grid">
            {renderShiftCard({
              title: "Strips",
              icon: "🍋",
              badge: "Output",
              badgeStyle: { background: "#dcfae6", color: "#067647" },
              rows: stripsItems,
            })}
            {renderShiftCard({
              title: "Cheeks",
              icon: "🥭",
              badge: "Output",
              badgeStyle: { background: "#dcfae6", color: "#067647" },
              rows: cheeksItems,
            })}
            {renderShiftCard({
              title: "Trash",
              icon: "🗑️",
              badge: "Loss",
              badgeStyle: { background: "#fee4e2", color: "#d92d20" },
              rows: trashItems,
            })}
            {renderShiftCard({
              title: "Total",
              icon: "📋",
              badge: "Summary",
              badgeStyle: { background: "#fff0ea", color: "#ee7225" },
              rows: totalItems,
            })}
          </div>
        </DashboardSection>
        {/* ── Group 2: Performance Metrics ─────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><BarChartIconSvg size={18} color="#60a5fa" />{_t("performance_metrics", "Performance Metrics")}</span>}>
          <div className="pp-shifts-grid">
            {renderShiftCard({
              title: "Processing Metrics - Case Based",
              icon: "📊",
              badge: "KPI",
              badgeStyle: { background: "#dfeaff", color: "#175cd3" },
              rows: metricsItems,
            })}
            {renderShiftCard({
              title: "Peeler Metrics",
              icon: "👷",
              badge: "Rate",
              badgeStyle: { background: "#dfeaff", color: "#175cd3" },
              rows: peelerMetricsItems,
            })}
          </div>
        </DashboardSection>
        {/* ── Group 4: Utility Tracking ─────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><UtilityIconSvg size={18} color="#fb923c" />{_t("utility_tracking", "Utility Tracking")}</span>}>
          <div className="db-avg-card">

            {/* Zone 1: Oven readings grid */}
            <div className="db-avg-header">
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <FlameIconSvg size={15} color="#fb923c" />{_t("oven_readings", "Oven Readings")}
              </span>
              <span style={{ fontSize: 12, color: "#667085", fontFamily: "'Public Sans', serif" }}>
                {_t("liters_consumed", "Liters Consumed")}
              </span>
            </div>
            <div className="pp-brokering-grid" style={{ padding: "16px 18px" }}>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="pp-brokering-box">
                  <div className="pp-brokering-box-label">Oven {n}</div>
                  <div className="pp-brokering-box-value">{formatMetric(getOvenLiters(n))}</div>
                </div>
              ))}
              <div className="pp-brokering-box" style={{ borderColor: "#e0e2e7" }}>
                <div className="pp-brokering-box-label">Evaporator</div>
                <div className="pp-brokering-box-value">{formatMetric(parseNumber(gasRecord?.evaperator))}</div>
              </div>
              <div className="pp-brokering-box" style={{ background: "#fff0ea", borderColor: "#f5d9c8" }}>
                <div className="pp-brokering-box-label" style={{ color: "#ee7225" }}>Total Liters</div>
                <div className="pp-brokering-box-value" style={{ color: "#ee7225" }}>{formatMetric(totalLitersUsed)}</div>
              </div>
            </div>

            {/* Zone 2: Cost breakdown */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <CostIconSvg size={15} color="#4ade80" />{_t("cost_breakdown", "Cost Breakdown")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">Price / Liter (with IVA)</div>
                <div className="db-metric-value">{formatMetric(pricePerLiterWithIva)}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">IVA Gas Paid</div>
                <div className="db-metric-value">{formatMetric(ivaGasPaid)}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Total Gas Paid</div>
                <div className="db-metric-value" style={{ color: "#ee7225" }}>{formatMetric(totalGasPaid)}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">USD</div>
                <div className="db-metric-value">{formatMetric(usdValue)}</div>
              </div>
            </div>

            {/* Zone 3: Efficiency KPIs */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <BarChartIconSvg size={15} color="#60a5fa" />{_t("efficiency_kpis", "Efficiency KPIs")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">KG Processed</div>
                <div className="db-metric-value">{formatMetric(kgProcessed)}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Gas Cost / KG</div>
                <div className="db-metric-value" style={{ color: "#ee7225" }}>{formatMetric(gasCostPerKg)}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Weekly Average</div>
                <div className="db-metric-value">{formatMetric(weeklyAverage)}</div>
              </div>
            </div>

          </div>
        </DashboardSection>

      </div>
    </div>
  );
};

export default ProcessingDashboard;
